import { useEffect, useMemo, useState } from 'react';
import { matchProductCodes, type ProductCodeMatchItem } from '@/api/product';

export interface PasteCodeLine {
  code: string;
  quantity: number;
  remark: string;
  matched: ProductCodeMatchItem;
}

/** 未匹配行（含原始粘贴行号） */
export interface PasteMissedItem {
  line: number;
  code: string;
}

interface PasteCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (lines: PasteCodeLine[], missed: PasteMissedItem[]) => void;
}

const REMARK_MAX = 200;

function toLines(text: string): string[] {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');
}

function splitCodeRows(text: string): Array<{ line: number; code: string }> {
  return toLines(text)
    .map((value, index) => ({ line: index + 1, code: value.trim() }))
    .filter((row) => row.code.length > 0);
}

function parseQuantity(raw: string): number | null {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return 1;
  const n = Math.floor(Number(trimmed));
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

function formatMissed(missed: PasteMissedItem[]) {
  return missed.map((item) => `第${item.line}行「${item.code}」`).join('、');
}

export default function PasteCodesModal({ isOpen, onClose, onConfirm }: PasteCodesModalProps) {
  const [codesText, setCodesText] = useState('');
  const [qtyText, setQtyText] = useState('');
  const [remarkText, setRemarkText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [missed, setMissed] = useState<PasteMissedItem[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setCodesText('');
      setQtyText('');
      setRemarkText('');
      setLoading(false);
      setError('');
      setMissed([]);
    }
  }, [isOpen]);

  const codeRows = useMemo(() => splitCodeRows(codesText), [codesText]);
  const qtyLines = useMemo(() => toLines(qtyText), [qtyText]);
  const remarkLines = useMemo(() => toLines(remarkText), [remarkText]);
  const filledQtyCount = useMemo(
    () => qtyLines.filter((v) => v.trim().length > 0).length,
    [qtyLines],
  );
  const filledRemarkCount = useMemo(
    () => remarkLines.filter((v) => v.trim().length > 0).length,
    [remarkLines],
  );

  const preview = useMemo(() => {
    return codeRows.map((row) => {
      const qtyRaw = qtyLines[row.line - 1] ?? '';
      const remarkRaw = (remarkLines[row.line - 1] ?? '').trim().slice(0, REMARK_MAX);
      return {
        line: row.line,
        code: row.code,
        quantity: parseQuantity(qtyRaw),
        qtyRaw: qtyRaw.trim(),
        remark: remarkRaw,
      };
    });
  }, [codeRows, qtyLines, remarkLines]);

  const handleConfirm = async () => {
    if (loading) return;
    setError('');
    setMissed([]);

    if (codeRows.length === 0) {
      setError('请粘贴物资编码（每行一个）');
      return;
    }

    const pairs: Array<{ line: number; code: string; quantity: number; remark: string }> = [];
    for (const row of codeRows) {
      const qtyRaw = qtyLines[row.line - 1] ?? '';
      const quantity = parseQuantity(qtyRaw);
      if (quantity == null) {
        setError(`第 ${row.line} 行数量无效：${qtyRaw.trim() || '(空)'}`);
        return;
      }
      const remark = (remarkLines[row.line - 1] ?? '').trim().slice(0, REMARK_MAX);
      pairs.push({ line: row.line, code: row.code, quantity, remark });
    }

    // 同编码合并：数量累加；备注去重拼接
    const mergedMap = new Map<string, { quantity: number; remark: string }>();
    pairs.forEach(({ code, quantity, remark }) => {
      const prev = mergedMap.get(code);
      if (!prev) {
        mergedMap.set(code, { quantity, remark });
        return;
      }
      const remarks = [prev.remark, remark].filter(Boolean);
      const uniqueRemarks = Array.from(new Set(remarks));
      mergedMap.set(code, {
        quantity: prev.quantity + quantity,
        remark: uniqueRemarks.join('；').slice(0, REMARK_MAX),
      });
    });
    const uniqueCodes = Array.from(mergedMap.keys());

    setLoading(true);
    try {
      const res = await matchProductCodes(uniqueCodes);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setError(res.message || res.msg || '编码匹配失败');
        return;
      }

      const matchedList = Array.isArray(res.data?.matched) ? res.data!.matched! : [];
      const missedCodes = new Set(
        (Array.isArray(res.data?.missed) ? res.data!.missed! : []).map(String),
      );
      const byCode = new Map(matchedList.map((item) => [String(item.code), item]));

      const missedItems: PasteMissedItem[] = pairs
        .filter((p) => missedCodes.has(p.code) || !byCode.has(p.code))
        .map((p) => ({ line: p.line, code: p.code }));

      const lines: PasteCodeLine[] = [];
      uniqueCodes.forEach((code) => {
        const hit = byCode.get(code);
        if (!hit) return;
        const merged = mergedMap.get(code)!;
        lines.push({
          code,
          quantity: merged.quantity,
          remark: merged.remark,
          matched: hit,
        });
      });

      setMissed(missedItems);
      if (lines.length === 0) {
        setError(
          missedItems.length > 0
            ? `以下物资找不到：${formatMissed(missedItems)}`
            : '未匹配到任何物资',
        );
        return;
      }

      onConfirm(lines, missedItems);

      // 有不匹配项时保持窗口，并仅保留未匹配行便于修改
      if (missedItems.length === 0) {
        onClose();
      } else {
        const missedLineSet = new Set(missedItems.map((item) => item.line));
        const nextCodes = toLines(codesText).map((value, index) => {
          const line = index + 1;
          if (!missedLineSet.has(line)) return '';
          return value;
        });
        const nextQtys = toLines(qtyText).map((value, index) => {
          const line = index + 1;
          if (!missedLineSet.has(line)) return '';
          return value;
        });
        const nextRemarks = toLines(remarkText).map((value, index) => {
          const line = index + 1;
          if (!missedLineSet.has(line)) return '';
          return value;
        });
        // 压缩掉已匹配的空行，保留未匹配内容的相对顺序
        const keepIndexes = nextCodes
          .map((v, i) => (v.trim() ? i : -1))
          .filter((i) => i >= 0);
        setCodesText(keepIndexes.map((i) => nextCodes[i].trim()).join('\n'));
        setQtyText(keepIndexes.map((i) => (nextQtys[i] ?? '').trim()).join('\n'));
        setRemarkText(keepIndexes.map((i) => (nextRemarks[i] ?? '').trim()).join('\n'));
        setMissed(
          keepIndexes.map((srcIdx, i) => ({
            line: i + 1,
            code: nextCodes[srcIdx].trim(),
          })),
        );
        setError(
          `已加入 ${lines.length} 项；以下物资找不到（原粘贴行号）：${formatMissed(missedItems)}`,
        );
      }
    } catch {
      setError('网络异常，编码匹配失败');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background-50 rounded-xl w-full max-w-5xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">粘贴物资编码</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              按行对应填写编码、数量、备注（数量/备注可留空；数量默认 1）
            </p>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors disabled:opacity-50"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="min-w-0">
              <label className="text-xs font-medium text-foreground-700 mb-1.5 flex items-center gap-1">
                <i className="ri-barcode-line text-foreground-400" />
                物资编码
                <span className="text-foreground-400 font-normal">（{codeRows.length} 行）</span>
              </label>
              <textarea
                value={codesText}
                disabled={loading}
                onChange={(e) => setCodesText(e.target.value)}
                rows={12}
                placeholder={'例如：\nSKU001\nSKU002\nSKU003'}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm font-mono outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100 leading-6"
              />
            </div>
            <div className="min-w-0">
              <label className="text-xs font-medium text-foreground-700 mb-1.5 flex items-center gap-1">
                <i className="ri-hashtag text-foreground-400" />
                采购数量
                <span className="text-foreground-400 font-normal">（{filledQtyCount} 行）</span>
              </label>
              <textarea
                value={qtyText}
                disabled={loading}
                onChange={(e) => setQtyText(e.target.value)}
                rows={12}
                placeholder={'例如：\n10\n5\n20'}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm font-mono outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100 leading-6"
              />
            </div>
            <div className="min-w-0">
              <label className="text-xs font-medium text-foreground-700 mb-1.5 flex items-center gap-1">
                <i className="ri-chat-1-line text-foreground-400" />
                物资备注
                <span className="text-foreground-400 font-normal">（{filledRemarkCount} 行）</span>
              </label>
              <textarea
                value={remarkText}
                disabled={loading}
                onChange={(e) => setRemarkText(e.target.value)}
                rows={12}
                placeholder={'例如：\n加急\n备货\n'}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm font-mono outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100 leading-6"
              />
            </div>
          </div>

          {preview.length > 0 && (
            <div className="rounded-lg border border-background-200 bg-white max-h-36 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-background-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium text-foreground-500">行</th>
                    <th className="text-left px-3 py-1.5 font-medium text-foreground-500">编码</th>
                    <th className="text-right px-3 py-1.5 font-medium text-foreground-500">数量</th>
                    <th className="text-left px-3 py-1.5 font-medium text-foreground-500">备注</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 50).map((row) => (
                    <tr key={`${row.line}-${row.code}`} className="border-t border-background-100">
                      <td className="px-3 py-1 text-foreground-400">{row.line}</td>
                      <td className="px-3 py-1 text-foreground-800 font-mono">{row.code}</td>
                      <td
                        className={`px-3 py-1 text-right font-mono ${
                          row.quantity == null ? 'text-red-500' : 'text-foreground-700'
                        }`}
                      >
                        {row.qtyRaw
                          ? row.quantity == null
                            ? row.qtyRaw
                            : row.quantity
                          : '1（默认）'}
                      </td>
                      <td className="px-3 py-1 text-foreground-600 max-w-[12rem] truncate">
                        {row.remark || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 50 && (
                <p className="px-3 py-1.5 text-[11px] text-foreground-400 border-t border-background-100">
                  仅预览前 50 行，共 {preview.length} 行
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-600 flex items-start gap-1.5">
              <i className="ri-error-warning-line shrink-0 mt-0.5" />
              <span className="break-all">{error}</span>
            </div>
          )}
          {!error && missed.length > 0 && (
            <div className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-700 flex items-start gap-1.5">
              <i className="ri-information-line shrink-0 mt-0.5" />
              <span className="break-all">找不到物资：{formatMissed(missed)}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            disabled={loading || codeRows.length === 0}
            onClick={() => void handleConfirm()}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-file-search-line" />}
            {loading ? '匹配中...' : '匹配并加入清单'}
          </button>
        </div>
      </div>
    </div>
  );
}
